/* Pricing / duration helpers for the structured tour model (v2).
   All money is handled in integer cents coming from the API. */

export const centsToEuro = (cents) => {
  const n = Number(cents || 0) / 100;
  const hasCents = Math.round(n * 100) % 100 !== 0;
  return `€${n.toLocaleString("it-IT", { minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: 2 })}`;
};

export const tourPricing = (tour) =>
  tour?.pricing || { mode: "per_person", base_cents: Math.round(Number(tour?.price || 0) * 100), base_participants: 1, extra_person_cents: null };

export const isPerGroup = (tour) => tourPricing(tour).mode === "per_group";

/* Returns { total_cents, quoteRequired } for a number of guests, mirroring the backend engine. */
export const computeTotal = (tour, guests) => {
  const p = tourPricing(tour);
  const base = Number(p.base_cents || 0);
  if (p.mode === "per_group") {
    const basePax = Number(p.base_participants || 1);
    if (guests <= basePax) return { total_cents: base, quoteRequired: false };
    if (p.extra_person_cents !== null && p.extra_person_cents !== undefined) {
      return { total_cents: base + (guests - basePax) * Number(p.extra_person_cents), quoteRequired: false };
    }
    return { total_cents: null, quoteRequired: true };
  }
  return { total_cents: base * guests, quoteRequired: false };
};

/* Human label for the price unit: "per person" or "per group (up to 2)" */
export const priceUnitLabel = (tour, t) => {
  const p = tourPricing(tour);
  if (p.mode === "per_group") {
    return Number(p.base_participants || 1) > 1
      ? t("price.perGroupUpTo", { n: p.base_participants })
      : t("price.perGroupBase1");
  }
  return t("card.pp");
};

export const basePriceEuro = (tour) => centsToEuro(tourPricing(tour).base_cents);

const fmtHours = (minutes) => {
  const h = minutes / 60;
  return Number.isInteger(h) ? String(h) : h.toLocaleString("it-IT", { maximumFractionDigits: 1 });
};

/* "2 - 3,5 hours" or "4 hours" */
export const durationLabel = (tour, t) => {
  const d = tour?.duration;
  if (!d) return t("card.hours", { n: tour?.duration_hours });
  if (d.min_minutes === d.max_minutes) {
    return d.max_minutes < 60 ? t("dur.minutes", { n: d.max_minutes }) : t("card.hours", { n: fmtHours(d.max_minutes) });
  }
  return t("dur.range", { a: fmtHours(d.min_minutes), b: fmtHours(d.max_minutes) });
};

export const capacity = (tour) => {
  if (tour?.capacity && "max_participants" in tour.capacity) return tour.capacity.max_participants || null;
  return tour?.max_guests || null;
};

/* Participants hint for cards/detail: capacity, private base size or tour type */
export const participantsLabel = (tour, t) => {
  const cap = capacity(tour);
  if (cap) return t("card.max", { n: cap });
  if (isPerGroup(tour)) {
    const n = tourPricing(tour).base_participants || 1;
    return t("price.privateFor", { n });
  }
  return t(`type.${tour?.tour_type || "group"}`);
};

export const LANG_NAMES = { it: "Italiano", en: "English", fr: "Français", es: "Español" };
